import { DataGrid } from "@mui/x-data-grid";
import {
  TextField,
  Button,
  Box,
  InputAdornment,
  Tooltip,
  Modal,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useState } from "react";
import ModeEditIcon from "@mui/icons-material/ModeEdit";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import { Spinner } from "react-bootstrap";

export default function CrudTable({
  columns,
  rows,
  loading = false,
  onRefresh,
  createButtonText = "Add",
  deleteButtonText = "Delete",
  FormComponent,
  handleSubmit,
  buttonLoading = false,
  handleDelete,
  onUpdate,
  getRowId,
  disable,
  formwidth = 500,
  academicYears = {},
  coordinators = [],
  participants = [],
  departments = [],
  resolvers = {},
  classSections=[],
}) {
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);



  const handleAdd = () => {
    setSelectedRow(null);
    setOpen(true);
  };

  const handleEdit = (row) => {
    setSelectedRow(row);
    setOpen(true);
  };

  const handleDeleteRow = (row) => {
    if (handleDelete) {
      handleDelete(row);
    }
  };

  const filteredRows = rows.filter((row) => {
    return columns.some((col) => {
      const rawValue = row[col.field];
      const resolvedValue = resolvers?.[col.field]
        ? resolvers[col.field](rawValue)
        : rawValue;
      return String(resolvedValue ?? "")
        .toLowerCase()
        .includes(search.toLowerCase());
    });
  });

  const handleClose = () => {
    setOpen(false);
    setSelectedRow(null);
  };

  const actionColumn = {
    field: "actions",
    headerName: "Actions",
    width: 100,
    headerClassName: "table-header",
    align: "center",
    headerAlign: "center",
    sortable: false,
    renderCell: (params) => (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
        }}
      >
        <Stack direction="row" spacing={0}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              onClick={() => handleEdit(params.row)}
              disabled={disable}
            >
              <ModeEditIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              disabled={disable}
              onClick={() => {
                setRowToDelete(params.row);
                setDeleteOpen(true);
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
      </Box>
    ),
  };

  const onSubmit = async (data) => {
    let success;
    if (selectedRow) {
      success = await onUpdate(data);
    } else {
      success = await handleSubmit(data);
    }

    if (success) handleClose();
  };

  return (
    <Box
      sx={{
        backgroundColor: "#ffffff",
        borderRadius: "12px",
        padding: "16px",
        border: "1px solid #e5e7eb",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
        }}
      >
        <TextField
          size="small"
          placeholder="Search..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            width: "260px",
            backgroundColor: "#fff",
            borderRadius: "8px",
          }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            },
          }}
        />

        <Box>
          <Tooltip title="Refresh">
            <Button
              onClick={onRefresh}
              variant="outlined"
              sx={{
                textTransform: "none",
                borderRadius: "50%",
                boxShadow: "none",
                width: "30px",
                height: "30px",
                minWidth: "30px",
                marginRight: "8px",
              }}
            >
              <RefreshIcon />
            </Button>
          </Tooltip>
          <Button
            onClick={handleAdd}
            disabled={disable}
            variant="contained"
            startIcon={<AddIcon />}
            sx={{
              textTransform: "none",
              borderRadius: "8px",
              boxShadow: "none",
              backgroundColor: "rgb(35, 71, 159)",
            }}
          >
            {createButtonText}
          </Button>
        </Box>
      </Box>

      <Box sx={{ width: "100%" }}>
        {loading ? (
          <div className="text-center p-4">
            <Spinner animation="border" />
          </div>
        ) : (
          <DataGrid
            rows={filteredRows}
            disableColumnMenu
            columns={columns.concat(actionColumn)}
            getRowId={getRowId}
            pageSizeOptions={[5, 10, 20, 50]}
            localeText={{
              noRowsLabel: "No data found",
            }}
            initialState={{
              pagination: {
                paginationModel: { pageSize: 5 },
              },
            }}
            disableRowSelectionOnClick
            sx={{
              border: "none",

              "& .table-header": {
                backgroundColor: "rgba(34, 75, 153, 0.1)",
                color: "rgb(34, 77, 153)",
                fontWeight: 700,
                fontSize: "14px",
                borderBottom: "1px solid #e2e8f0",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: "bold",
              },

              "& .MuiDataGrid-cell": {
                borderBottom: "1px solid #f1f5f9",
                fontSize: "14px",
                color: "#0f172a",
              },

              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#f8fafc",
              },

              "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": {
                outline: "none",
              },

              "& .MuiTablePagination-root": {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "8px 16px",
              },

              "& .MuiTablePagination-toolbar": {
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              },

              "& .MuiTablePagination-selectLabel": {
                margin: 0,
              },

              "& .MuiTablePagination-displayedRows": {
                margin: 0,
              },

              "& .MuiTablePagination-actions": {
                display: "flex",
                alignItems: "center",
              },
            }}
          />
        )}
      </Box>
      <Modal open={open} onClose={handleClose}>
        <Box
          sx={{
            p: 3,
            bgcolor: "#fff",
            width: formwidth,
            borderRadius: 2,
            mx: "auto",
            mt: "10%",
          }}
        >
          <FormComponent
            initialData={selectedRow}
            onCancel={handleClose}
            buttonLoading={buttonLoading}
            onSubmit={onSubmit}
            academicYears={academicYears}
            coordinators={coordinators}
            participants={participants}
            departments={departments}
            classSections={classSections}
          />
        </Box>
      </Modal>
      <Modal open={deleteOpen} onClose={() => setDeleteOpen(false)}>
        <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)}>
          <DialogTitle sx={{ fontWeight: 600 }}>{deleteButtonText}</DialogTitle>

          <DialogContent>
            <Typography sx={{ color: "#64748b" }}>
              Are you sure you want to delete{" "}
              <b>{rowToDelete?.name || "this record"}</b>? This action cannot be
              undone.
            </Typography>
          </DialogContent>

          <DialogActions>
            <Button
              onClick={() => setDeleteOpen(false)}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>

            <Button
              color="error"
              variant="contained"
              onClick={() => {
                handleDeleteRow(rowToDelete);
                setDeleteOpen(false);
                setRowToDelete(null);
              }}
              sx={{ textTransform: "none" }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      </Modal>
    </Box>
  );
}
